import { PreferencesHeader } from "@bcc/components/preferences";
import { Alert, AlertDescription, AlertTitle } from "@bcc/components/ui/alert";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { Label } from "@bcc/components/ui/label";
import { useToast } from "@bcc/hooks/use-toast";
import { cn } from "@bcc/lib/utils";
import useVendorContext from "@client-shared/providers/vendor";
import { isLabelPrintingEnabled } from "@client-shared/utilities/isLabelPrintingEnabled";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as RadioGroup from "@radix-ui/react-radio-group";
import type { GroupOption } from "@shared/types";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setPrintingPreferencesViewTestId } from "./printing-preferences-view.utils";

const PAPER_SIZES = [
  {
    value: "4x6",
    labelKey: "views.preferences.printing.paperSize.4x6.label",
    descKey: "views.preferences.printing.paperSize.4x6.description",
    testId: "paperSize4x6",
  },
  {
    value: "3x4",
    labelKey: "views.preferences.printing.paperSize.3x4.label",
    descKey: "views.preferences.printing.paperSize.3x4.description",
    testId: "paperSize3x4",
  },
  {
    value: "A6",
    labelKey: "views.preferences.printing.paperSize.a6.label",
    descKey: "views.preferences.printing.paperSize.a6.description",
    testId: "paperSizeA6",
  },
  {
    value: "A4",
    labelKey: "views.preferences.printing.paperSize.a4.label",
    descKey: "views.preferences.printing.paperSize.a4.description",
    testId: "paperSizeA4",
  },
  {
    value: "Letter",
    labelKey: "views.preferences.printing.paperSize.letter.label",
    descKey: "views.preferences.printing.paperSize.letter.description",
    testId: "paperSizeLetter",
  },
] as const;

const GROUP_BY_OPTIONS = [
  {
    value: "product" as GroupOption,
    labelKey: "views.preferences.printing.groupBy.product.label",
    descKey: "views.preferences.printing.groupBy.product.description",
    testId: "groupByProduct",
  },
  {
    value: "grind" as GroupOption,
    labelKey: "views.preferences.printing.groupBy.grind.label",
    descKey: "views.preferences.printing.groupBy.grind.description",
    testId: "groupByGrind",
  },
  {
    value: "quantity" as GroupOption,
    labelKey: "views.preferences.printing.groupBy.quantity.label",
    descKey: "views.preferences.printing.groupBy.quantity.description",
    testId: "groupByQuantity",
  },
] as const;

const TILE_CLASSES =
  "flex flex-col gap-1 rounded-lg border p-4 cursor-pointer text-left data-[state=checked]:border-primary data-[state=checked]:bg-primary/5 data-[state=unchecked]:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type BatchPrintPreference = "LabelsOnly" | "LabelsWithPackingSlips";

function BatchPrintDiagram({ variant }: { readonly variant: "labelsOnly" | "labelsWithPackingSlips" }) {
  const { t } = useTranslation();
  const labelText = t("views.preferences.printing.batchPrinting.diagram.label");
  const slipText = t("views.preferences.printing.batchPrinting.diagram.slip");

  return (
    <div
      className="mt-3 flex w-24 flex-col rounded-lg border-2 border-dashed border-foreground p-0.5"
      style={{ height: "12rem" }}
      aria-hidden="true"
    >
      <div className="m-0.5 flex flex-1 flex-col bg-foreground text-center text-[8px] uppercase leading-relaxed text-white">
        <span className="flex-1 mx-0 border border-foreground bg-white" />
        <span>{labelText}</span>
        <span className="flex-1 mx-0 border border-foreground bg-white" />
      </div>
      <div
        className={cn(
          "m-0.5 flex flex-1 flex-col text-center text-[8px] uppercase leading-relaxed text-white",
          variant === "labelsWithPackingSlips" ? "bg-primary" : "bg-foreground"
        )}
      >
        <span
          className={cn("flex-1 mx-0 bg-white border", variant === "labelsWithPackingSlips" ? "border-primary" : "border-foreground")}
        />
        <span>{variant === "labelsWithPackingSlips" ? slipText : labelText}</span>
        <span
          className={cn("flex-1 mx-0 bg-white border", variant === "labelsWithPackingSlips" ? "border-primary" : "border-foreground")}
        />
      </div>
    </div>
  );
}

export function PrintingPreferencesView() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [vendorState, , utils] = useVendorContext();
  const { currentVendor, loading } = vendorState;

  const testIds = useMemo(() => setPrintingPreferencesViewTestId(), []);

  const persisted = useMemo(() => {
    const rawProperties = currentVendor?.properties;
    return {
      batchPrintPreference: (rawProperties?.batchPrintPreference as BatchPrintPreference) ?? "LabelsOnly",
      defaultPrintSize: rawProperties?.defaultPrintSize || "4x6",
      defaultGroupBySettings: rawProperties?.defaultGroupBySettings ?? [],
      displayPoNumberOnShippingLabels: rawProperties?.displayPoNumberOnShippingLabels ?? false,
    };
  }, [currentVendor]);

  const [properties, setProperties] = useState<{
    batchPrintPreference: BatchPrintPreference;
    defaultPrintSize: string;
    defaultGroupBySettings: GroupOption[];
    displayPoNumberOnShippingLabels: boolean;
  }>(persisted);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync printing preferences from current vendor properties on vendor change
    setProperties({
      batchPrintPreference: (currentVendor?.properties?.batchPrintPreference as BatchPrintPreference) ?? "LabelsOnly",
      defaultPrintSize: currentVendor?.properties?.defaultPrintSize || "4x6",
      defaultGroupBySettings: currentVendor?.properties?.defaultGroupBySettings ?? [],
      displayPoNumberOnShippingLabels: currentVendor?.properties?.displayPoNumberOnShippingLabels ?? false,
    });
  }, [currentVendor]);

  const isSaveEnabled = useMemo(() => {
    if (loading || !utils) return false;
    const groupByChanged =
      properties.defaultGroupBySettings.length !== persisted.defaultGroupBySettings.length ||
      properties.defaultGroupBySettings.some((opt) => !persisted.defaultGroupBySettings.includes(opt));
    return (
      properties.batchPrintPreference !== persisted.batchPrintPreference ||
      properties.defaultPrintSize !== persisted.defaultPrintSize ||
      groupByChanged ||
      properties.displayPoNumberOnShippingLabels !== persisted.displayPoNumberOnShippingLabels
    );
  }, [loading, utils, persisted, properties]);

  const handleGroupByToggle = useCallback((option: GroupOption) => {
    setProperties((prev) => ({
      ...prev,
      defaultGroupBySettings: prev.defaultGroupBySettings.includes(option)
        ? prev.defaultGroupBySettings.filter((o) => o !== option)
        : [...prev.defaultGroupBySettings, option],
    }));
  }, []);

  const handleDisplayPoNumberChange = useCallback((checked: boolean | "indeterminate") => {
    setProperties((prev) => ({ ...prev, displayPoNumberOnShippingLabels: checked === true }));
  }, []);

  const groupByHelperText = useMemo(() => {
    const selected = properties.defaultGroupBySettings;
    if (selected.length === 0) {
      return t("views.preferences.printing.groupBy.helperText.default");
    }
    const optionLabels = selected.map((opt) => t(`views.preferences.printing.groupBy.${opt}.label`));
    if (selected.length === 1) {
      return t("views.preferences.printing.groupBy.helperText.single", {
        option: optionLabels[0],
      });
    }
    if (selected.length === 2) {
      return t("views.preferences.printing.groupBy.helperText.multiple", {
        first: optionLabels[0],
        second: optionLabels[1],
      });
    }
    return t("views.preferences.printing.groupBy.helperText.multipleThree", {
      first: optionLabels[0],
      second: optionLabels[1],
      third: optionLabels[2],
    });
  }, [properties.defaultGroupBySettings, t]);

  const handleSave = useCallback(async () => {
    const validPrintSizes = ["A6", "A4", "Letter", "4x6", "3x4"] as const;
    type ValidPrintSize = (typeof validPrintSizes)[number];
    const isValidPrintSize = (validPrintSizes as readonly string[]).includes(properties.defaultPrintSize);
    setIsSaving(true);
    try {
      const result = await utils?.updateProperties({
        batchPrintPreference: properties.batchPrintPreference,
        ...(isValidPrintSize && {
          defaultPrintSize: properties.defaultPrintSize as ValidPrintSize,
        }),
        defaultGroupBySettings: properties.defaultGroupBySettings,
        displayPoNumberOnShippingLabels: properties.displayPoNumberOnShippingLabels,
      });
      if (result) {
        toast({
          title: t("views.preferences.printing.saveSuccess.title"),
          description: t("views.preferences.printing.saveSuccess.description"),
        });
      } else {
        toast({
          title: t("views.preferences.printing.saveError.title"),
          description: t("views.preferences.printing.saveError.description"),
        });
      }
    } catch {
      toast({
        title: t("views.preferences.printing.saveError.title"),
        description: t("views.preferences.printing.saveError.description"),
      });
    } finally {
      setIsSaving(false);
    }
  }, [utils, properties, toast, t]);

  if (!isLabelPrintingEnabled(currentVendor)) {
    return (
      <Alert variant="default" data-testid={testIds.notAvailableAlert}>
        <AlertTitle>{t("views.preferences.printing.notAvailableTitle")}</AlertTitle>
        <AlertDescription>{t("views.preferences.printing.notAvailableDescription")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <PreferencesHeader />

      <Card data-testid={testIds.container}>
        <CardHeader>
          <CardTitle>{t("views.preferences.printing.title")}</CardTitle>
          <CardDescription>{t("views.preferences.printing.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <section data-testid={testIds.batchPrintSection}>
            <h3 id="batch-printing-heading" className="text-sm font-bold mb-3">
              {t("views.preferences.printing.batchPrinting.title")}
            </h3>
            <RadioGroup.Root
              aria-labelledby="batch-printing-heading"
              value={properties.batchPrintPreference}
              onValueChange={(value) =>
                setProperties((prev) => ({
                  ...prev,
                  batchPrintPreference: value as BatchPrintPreference,
                }))
              }
              data-testid={testIds.batchPrintGroup}
              className="flex gap-3"
            >
              <RadioGroup.Item value="LabelsOnly" data-testid={testIds.batchPrintLabelsOnly} className={cn(TILE_CLASSES, "max-w-[20rem]")}>
                <span className="font-medium text-sm">{t("views.preferences.printing.batchPrinting.labelsOnly.label")}</span>
                <span className="text-xs text-muted-foreground">
                  {t("views.preferences.printing.batchPrinting.labelsOnly.description")}
                </span>
                <BatchPrintDiagram variant="labelsOnly" />
              </RadioGroup.Item>
              <RadioGroup.Item
                value="LabelsWithPackingSlips"
                data-testid={testIds.batchPrintLabelsWithPackingSlips}
                className={cn(TILE_CLASSES, "max-w-[20rem]")}
              >
                <span className="font-medium text-sm">{t("views.preferences.printing.batchPrinting.labelsWithPackingSlips.label")}</span>
                <span className="text-xs text-muted-foreground">
                  {t("views.preferences.printing.batchPrinting.labelsWithPackingSlips.description")}
                </span>
                <BatchPrintDiagram variant="labelsWithPackingSlips" />
              </RadioGroup.Item>
            </RadioGroup.Root>
          </section>

          <section data-testid={testIds.paperSizeSection}>
            <h3 id="paper-size-heading" className="text-sm font-bold mb-3">
              {t("views.preferences.printing.paperSize.title")}
            </h3>
            <RadioGroup.Root
              aria-labelledby="paper-size-heading"
              value={properties.defaultPrintSize}
              onValueChange={(value) => setProperties((prev) => ({ ...prev, defaultPrintSize: value }))}
              data-testid={testIds.paperSizeGroup}
              className="flex flex-wrap gap-3"
            >
              {PAPER_SIZES.map(({ value, labelKey, descKey, testId }) => (
                <RadioGroup.Item
                  key={value}
                  value={value}
                  data-testid={testIds[testId as keyof typeof testIds]}
                  className={cn(TILE_CLASSES)}
                >
                  <span className="font-medium text-sm">{t(labelKey)}</span>
                  <span className="text-xs text-muted-foreground">{t(descKey)}</span>
                </RadioGroup.Item>
              ))}
            </RadioGroup.Root>
          </section>

          <section data-testid={testIds.groupBySection}>
            <h3 id="group-by-heading" className="text-sm font-bold mb-1">
              {t("views.preferences.printing.groupBy.title")}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">{t("views.preferences.printing.groupBy.description")}</p>
            <div className="flex flex-wrap gap-3">
              {GROUP_BY_OPTIONS.map(({ value, labelKey, descKey, testId }) => (
                <CheckboxPrimitive.Root
                  key={value}
                  checked={properties.defaultGroupBySettings.includes(value)}
                  onCheckedChange={() => handleGroupByToggle(value)}
                  data-testid={testIds[testId]}
                  className={TILE_CLASSES}
                >
                  <span className="font-medium text-sm">{t(labelKey)}</span>
                  <span className="text-xs text-muted-foreground">{t(descKey)}</span>
                </CheckboxPrimitive.Root>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">{groupByHelperText}</p>
          </section>

          <section data-testid={testIds.labelOptionsSection}>
            <h3 id="label-options-heading" className="text-sm font-bold mb-1">
              {t("views.preferences.printing.labelOptions.title")}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">{t("views.preferences.printing.labelOptions.description")}</p>
            <div className="flex items-center gap-3 py-1">
              <Checkbox
                id="display-po-number"
                checked={properties.displayPoNumberOnShippingLabels}
                onCheckedChange={handleDisplayPoNumberChange}
                testId={testIds.displayPoNumberToggle}
              />
              <Label htmlFor="display-po-number" className="text-sm font-medium cursor-pointer">
                {t("views.preferences.printing.labelOptions.displayPoNumber.label")}
              </Label>
            </div>
          </section>

          <Button onClick={handleSave} disabled={!isSaveEnabled || isSaving} data-testid={testIds.saveButton}>
            {isSaving && <Loader2 className="animate-spin" />}
            {isSaving ? t("views.preferences.printing.savingButton") : t("views.preferences.printing.saveButton")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
